import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import SettingsMenu from '../components/settings/SettingsMenu';
import { useAppSelector } from '../store/hooks';
import Button from '../UI/buttons/Button';
import FormItem from '../components/form/FormItem';
import RadioButtonTab from '../UI/buttons/RadioButtonTab';
import {
    updateRecruiter,
    updateRecruiterCompany,
    updateCompanyVirtualStatus,
    getRecruiterInfo
} from '../api/recruiter';
import Input from '../UI/inputs/Input';
import Textarea from '../UI/inputs/Textarea';

// Re-use styled components from original file or define needed ones here
const PageWrapper = styled.section`
  background: #f9fafb;
  width: 100%;
  display: flex;
  min-height: 100vh;
  flex-flow: row-reverse;
`;

const FormWrapper = styled.div`
  background: #fff;
  width: 61%;
  padding: 4.5rem 4rem;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  margin: 24px;

  @media (max-width: 1200px) {
    width: 70%;
  }
  @media (max-width: 768px) {
    width: 90%;
    padding: 3rem;
    margin: 16px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const StyledForm = styled(Form)`
  margin-bottom: 3rem;
`;

const RecruiterCompanyTypeSettings = () => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const user = useAppSelector((state) => state.user.user);
    const [loading, setLoading] = useState(false);
    const [showCompanyField, setShowCompanyField] = useState(false);

    // Load initial data
    useEffect(() => {
        if (user) {
            form.setFieldsValue({
                recruiter_type: user.recruiter_type,
                company_name: user.company?.name,
            });
            const isAgency = user.recruiter_type === 'agency';
            setShowCompanyField(isAgency);
            if (isAgency) {
                getRecruiterInfo().then(res => {
                    form.setFieldsValue({
                        about_company: res.data.about_company
                    });
                }).catch(err => console.error("Failed to fetch recruiter info for about_company", err));
            }
        }
    }, [user, form]);

    const handleChangeRecruiterType = async (values: any) => {
        setLoading(true);

        try {
            // Simplified checks as this page handles only type and company info
            if (!user?.company?.id && values.recruiter_type === 'agency') {
                message.error(t('You need to be part of a company to set type to Agency'));
                setLoading(false);
                return;
            }
            if (values.recruiter_type === 'agency' && !values.company_name) {
                message.error(t('Please enter a company name'));
                setLoading(false);
                return;
            }

            const companyId = user?.company?.id;
            const typeChanged = values.recruiter_type !== user?.recruiter_type;
            const nameChanged = values.company_name !== user?.company?.name;
            // Re-fetch current about_company to compare accurately
            let currentAboutCompany = '';
            if (user && user.recruiter_type === 'agency') {
                try {
                    const info = await getRecruiterInfo();
                    currentAboutCompany = info.data.about_company;
                } catch (err) {
                    console.error("Failed to fetch current about_company for comparison", err);
                    // Proceed without comparison if fetch fails, maybe show warning
                }
            }
            const aboutChanged = (values.about_company || '') !== currentAboutCompany;

            // Skip if nothing changed
            if (!typeChanged && !nameChanged && !aboutChanged) {
                message.info(t('No changes to save'));
                setLoading(false);
                return;
            }

            // Handle company changes
            if (companyId && (typeChanged || nameChanged)) {
                const isVirtual = values.recruiter_type === 'freelance';
                if (values.recruiter_type === 'agency' && nameChanged) {
                    await updateRecruiterCompany(companyId, { name: values.company_name });
                } else if (typeChanged) {
                    await updateCompanyVirtualStatus(
                        companyId,
                        isVirtual,
                        isVirtual ? (user?.name || '') : values.company_name
                    );
                }
            }

            // Update recruiter profile if type or description changed
            if (typeChanged || aboutChanged) {
                await updateRecruiter({
                    recruiter_type: values.recruiter_type,
                    about_company: values.about_company || undefined
                });
            }

            message.success(t('Information updated successfully'));

            // Reload might be needed if fundamental company structure changes
            if (typeChanged || nameChanged) {
                window.location.reload(); // Consider if a less disruptive update is possible
            }
        } catch (error) {
            console.error('Error updating recruiter information', error);
            message.error(t('Failed to update information'));
        } finally {
            setLoading(false);
        }
    };

    const handleValueChange = (changedValues: any) => {
        if (changedValues.recruiter_type) {
            setShowCompanyField(changedValues.recruiter_type === 'agency');
        }
    };

    return (
        <PageWrapper>
            <FormWrapper>
                {/* Assuming SettingsMenu handles active state based on route */}
                <SettingsMenu menu="recruiter-company-type" />

                <PageHeader>
                    <PageTitle>{t('COMPANY_SETTINGS')}</PageTitle>
                </PageHeader>

                <StyledForm
                    form={form}
                    layout="vertical"
                    name="recruiter-type-form"
                    onFinish={handleChangeRecruiterType}
                    onValuesChange={handleValueChange}
                >
                    <FormItem
                        label={t('Recruiter Type')}
                        name="recruiter_type"
                        rules={[{ required: true, message: t('REQUIRED_ERROR') }]}
                    >
                        <RadioButtonTab
                            items={[
                                {
                                    name: t('Freelance'),
                                    value: 'freelance',
                                },
                                {
                                    name: t('Agency'),
                                    value: 'agency',
                                },
                            ]}
                        />
                    </FormItem>

                    {showCompanyField && (
                        <>
                            <FormItem
                                label={t('COMPANY_NAME_TITLE')}
                                name="company_name"
                                rules={[{ required: true, message: t('REQUIRED_ERROR') }]}
                            >
                                <Input size="large" />
                            </FormItem>
                            <FormItem label={`${t('ABOUT_YOUR_COMPANY')}`} name="about_company">
                                <Textarea autoSize={{ minRows: 3 }} />
                            </FormItem>
                        </>
                    )}

                    <FormItem>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                        >
                            {t('Save')}
                        </Button>
                    </FormItem>
                </StyledForm>
            </FormWrapper>
        </PageWrapper>
    );
};

export default RecruiterCompanyTypeSettings; 